import { NIGHTMARES } from '@somnhold/content';
import { hexToWorld } from '../hex.ts';
import { dist2, idiv, imax, imin, isqrt, mulBp, withinRange } from '../math/fixed.ts';
import { rngInt, rngRange } from '../math/rng.ts';
import type { BattleState, Enemy, ErmState, Hero, HermitRules, LitterRules, LitterState, SendaNpc, VelaRules, VelState } from '../types.ts';
import { damageEnemy, damageHero, ev, spawnEnemy } from './combat.ts';
import { attackHero, stepToward } from './enemies.ts';
import { orderMove } from './heroes.ts';
import { guardTick } from './keep.ts';
import { sendaCue } from './senda.ts';
import { naveTrace } from './ermita-layout.ts';

/**
 * The cleric's last road, chapter 5 (docs/sendas/clerigo.md, «La vigilia»; boards in battle/vigilia-layout.ts, rules in
 * battle/vigilia-rules.ts, numbers in content CAMINO.lamps, procession, mourn). Played on the stretcher's frame
 * (battle/litter.ts) through chapter 4's hooks (battle/ermita.ts hands its acts here when the nave carries `vela`):
 *
 * - The lamps (`lamps`, act 1): the six lamps of the vigil up the nave, one free each minute, lit by standing by it. A
 *   lamp lit (or lit again) peels with its ring of light the limpets near it. Limpets out of the ink on the walls go for a
 *   lit lamp and, stuck on it a while, put it out; the dead in from the cells go for one too and put it out with a few
 *   blows; gargoyles come down by the hole in the roof at the hero; a bone singer by the atrium. Lost with two lamps out
 *   at once; won with all six lit.
 * - The atrium (`procession`, act 2): the Doliente's procession in by the cliff's door, band after band, for the nave's
 *   door (Piet bars it: so many blows and it breaks). Joran and his apprentices fight in the middle; down, they wait for
 *   the hero. Won with the procession all in and down.
 * - The Doliente (`mourn`, act 4): she keeps off the candle by Tessa and sends her tongues of ink down the sides of the
 *   nave to it (one at a time; two from the split, one each side); a tongue held at the candle puts it out (lost). The
 *   beam burns what crosses it; Joran holds one at the candle now and then. Her wail (warned): no heal works; holy light on
 *   her in it cuts it, and she is quiet a while; not cut, the candle shrinks (the next tongue is quicker). Each wail raises
 *   limpets for Tessa (the truth), the door's people, and from the split for a mirror of the way. Her veil reaches anyone
 *   within bow's reach of her, through armour; her embrace (warned) whoever is close. At the split Ludo and the muleteers
 *   come in by the atrium (they clean a covered mirror with vinegar now and then; they fight what comes in after them). A
 *   mirror of the way frosts over (a brazier by it thaws it). At the first light the sun takes the way if it is set
 *   right: on her it burns, and in it she can't weep; from then she comes for the hero and weeps without pause outside it.
 *   Won at sunrise with the candle lit, or with her down.
 *
 * Cost: six lamps, a handful of people and nightmares looked at once a tick; the people's blows and the beam's burn once
 * a second; nothing walks every nightmare against every other (docs/RENDIMIENTO.md, «Reglas para lo nuevo»).
 */

/** The brazier by the frosted mirror (the `mark` command's `at`; the lamps are chapter 4's `ERM_LAMP + i`). */
export const VEL_BRAZIER = 70;
const VEL_LAMP = 20;
const NEVER = 0x7fffffff;
const HOLD = 1500;
const BLOW_R = 950;

type Pt = { x: number; y: number };
type O = { r: LitterRules; H: HermitRules; V: VelaRules; w: LitterState; m: ErmState; v: VelState };

export const velFresh = (V: VelaRules): VelState => ({
  // (Lit in act 1, they stay lit for the Doliente: the nave is seen from the terraces.)
  lamp: V.lamps.map(() => (V.kind === 'mourn' ? 1 : 0)),
  lampHits: V.lamps.map(() => 0),
  onLamp: [],
  next: V.lamp?.limpet.first ?? NEVER,
  dead: V.lamp?.dead.first ?? V.mourn?.dead.first ?? NEVER,
  garg: V.lamp?.garg.first ?? NEVER,
  singer: 0,
  done: -1,
  door: V.procession?.doorHits ?? 0,
  band: 0,
  bandAt: V.procession?.band.first ?? NEVER,
  swing: V.keeps.map(() => 0),
  lift: V.keeps.map(() => 0),
  phase: 0,
  tongueAt: V.mourn ? V.mourn.wail.first - V.mourn.wail.warn : NEVER,
  side: 0,
  tongues: [],
  hug: -1,
  hugOn: -1,
  hugAt: V.mourn?.hug.first ?? NEVER,
  joran: 0,
  candle: 1,
  sun: -1,
  vinegar: V.mourn ? V.mourn.split + V.mourn.vinegar : NEVER,
  frost: -1,
  thaw: 0,
  onPeople: [],
  bite: 0,
  cut: 0,
  came: 0,
});

const velOf = (s: BattleState): O | null => {
  const r = s.rules?.senda?.litter;
  const w = s.senda?.litter;
  const H = r?.hermit;
  const V = H?.vela;
  const m = w?.erm;
  const v = m?.vel;
  return r && H && V && w && m && v ? { r, H, V, w, m, v } : null;
};

const person = (s: BattleState, id: string): SendaNpc | undefined => s.senda?.npcs.find((n) => n.id === id);
const scaled = (s: BattleState, e: Enemy): Enemy => {
  if (s.rules?.enemyHpBp) e.hp = e.maxHp = imax(1, mulBp(e.maxHp, s.rules.enemyHpBp));
  return e;
};
const up = (n: SendaNpc | undefined): n is SendaNpc => !!n && n.hp > 0 && !n.away;
/** Never struck (its def's `safe`): who waits out of the fight. */
const safe = (s: BattleState, i: number): boolean => !!s.rules?.senda?.npcs[i]?.safe;
/** The ones who come in the Doliente's fight: away (and out of sight) until the split. */
export const VEL_COMERS = ['ludo', 'arriero', 'arrieroB'];
const away = (o: O, id: string): boolean => o.V.kind === 'mourn' && o.v.came === 0 && VEL_COMERS.includes(id);

/** A lamp free to light (its minute come). */
export const velLampReady = (V: VelaRules, i: number, waveT: number): boolean => !!V.lamp && waveT >= V.lamp.every * i;

/** The Doliente is weeping: no heal works. */
export const velNoHeal = (s: BattleState): boolean => {
  const o = velOf(s);
  return !!o && !!o.V.mourn && o.m.boss > 0 && s.waveT < o.m.wailUntil && s.enemies.some((e) => e.id === o.m.boss && e.hp > 0);
};

/** Holy light on the Doliente while she weeps counts toward hushing her; quiet, she takes more of what lands. */
export const velTaken = (s: BattleState, e: Enemy, amount: number, type: string | undefined): number => {
  const o = velOf(s);
  if (!o || !o.V.mourn || e.id !== o.m.boss) return amount;
  if (type === 'holy' && s.waveT < o.m.wailUntil) o.m.holy += amount;
  return s.waveT < o.m.mute ? mulBp(amount, o.V.mourn.wail.mutedBp) : amount;
};

/** A holy blow on a limpet stuck on one of the door's people peels it: it leaps at the hero (true: handled). */
export const velHoly = (s: BattleState, e: Enemy): boolean => {
  const o = velOf(s);
  if (!o) return false;
  const k = o.v.onPeople.findIndex((x) => x.id === e.id && x.on === 1);
  if (k < 0) return false;
  o.v.onPeople.splice(k, 1);
  o.w.limpets.push({ id: e.id, on: 3, t: s.tick });
  ev(s, { t: 'senda', kind: 'peel', npc: '', x: e.x, y: e.y });
  return true;
};

/* ---------- the marks ---------- */

/** The point of one of its own marks (a lamp, the brazier), or null. */
export const velMarkAt = (s: BattleState, at: number): Pt | null => {
  const o = velOf(s);
  if (!o) return null;
  if (at >= VEL_LAMP && at < VEL_LAMP + 6) return o.V.lamps[at - VEL_LAMP] ?? null;
  if (at === VEL_BRAZIER && o.V.mourn) return { ...o.V.mourn.frost.brazier };
  return null;
};

/** Sends the hero to a lamp or the brazier (true: one of its own; the mirrors are chapter 4's). */
export const velOrder = (s: BattleState, h: Hero, at: number): boolean => {
  const o = velOf(s);
  if (!o) return false;
  const p = velMarkAt(s, at);
  if (!p) return false;
  o.w.job = at;
  o.w.held = 0;
  // (Beside a lamp, on the floor; by the brazier, in front of it.)
  const to = at === VEL_BRAZIER ? { x: p.x, y: p.y + 350 } : (o.V.stands[at - VEL_LAMP] ?? p);
  orderMove(s, h, to.x, to.y);
  h.target = 0;
  h.forced = false;
  return true;
};

/** Its own work by a mark (true: it was one of its own, worked here). */
const work = (s: BattleState, o: O, h: Hero | undefined, cue: (c: string) => void, rebeam: () => void): boolean => {
  const { V, w, v } = o;
  const at = w.job;
  if (at < 0 || !(at === VEL_BRAZIER || (at >= VEL_LAMP && at < VEL_LAMP + 6))) return false;
  const p = velMarkAt(s, at);
  if (!p || !h) {
    w.job = -1;
    return true;
  }
  if (h.moving || !withinRange(h.x, h.y, p.x, p.y, o.H.reach)) {
    if (h.moving || w.held > 0) w.held = 0;
    else if (--w.held < -24) {
      w.held = 0;
      w.job = -1;
    }
    return true;
  }
  if (w.held < 0) w.held = 0;
  let need: number;
  if (at === VEL_BRAZIER) {
    if (!V.mourn || v.frost < 0) {
      w.job = -1;
      return true;
    }
    need = V.mourn.frost.thaw;
  } else {
    const i = at - VEL_LAMP;
    if (!V.lamp || v.lamp[i] === 1 || !velLampReady(V, i, s.waveT)) {
      w.job = -1;
      return true;
    }
    need = V.lamp.light;
  }
  if (++w.held < need) return true;
  w.held = 0;
  w.job = -1;
  if (at === VEL_BRAZIER) {
    ev(s, { t: 'senda', kind: 'thaw', npc: '', x: p.x, y: p.y, n: v.frost });
    v.frost = -1;
    cue('thaw');
    rebeam();
    return true;
  }
  kindle(s, o, at - VEL_LAMP, cue);
  return true;
};

/** A lamp lit (or lit again): its ring of light on the limpets near it. */
const kindle = (s: BattleState, o: O, i: number, cue: (c: string) => void): void => {
  const { V, v } = o;
  const A = V.lamp;
  const p = V.lamps[i];
  if (!A || !p) return;
  const again = v.lamp[i] === 2;
  v.lamp[i] = 1;
  v.lampHits[i] = 0;
  ev(s, { t: 'senda', kind: 'kindle', npc: '', x: p.x, y: p.y, n: i });
  for (const e of s.enemies)
    if (e.hp > 0 && e.kind === 'dragger' && withinRange(e.x, e.y, p.x, p.y, A.ring)) {
      ev(s, { t: 'senda', kind: 'peel', npc: '', x: e.x, y: e.y });
      damageEnemy(s, e, A.ringHp, { pid: '', type: 'holy' });
    }
  if (!again) cue(`lamp${i + 1}`);
  if (v.lamp.every((x) => x === 1) && v.done < 0) v.done = s.tick;
};

/* ---------- the people ---------- */

/** Where each person keeps (the ones who come walk in from past the atrium). */
const keepAt = (V: VelaRules, id: string): Pt | null => V.keeps.find((k) => k.id === id) ?? null;

/** Who fights beside the hero: Joran and the apprentices in the atrium; the apprentices, Ludo and the muleteers by the nave's door. */
const fights = (V: VelaRules, id: string): boolean =>
  V.kind === 'procession' ? id === 'joran' || id === 'apprenticeA' || id === 'apprenticeB' : V.kind === 'mourn' && id !== 'tessa' && id !== 'joran' && id !== 'piet';

const people = (s: BattleState, o: O, h: Hero | undefined, cue: (c: string) => void): void => {
  const { V, v } = o;
  const st = s.senda;
  if (!st) return;
  const G = V.procession?.guard ?? V.mourn?.guard;
  const lift = V.procession?.lift ?? V.mourn?.lift ?? 24;
  const liftBp = V.procession?.liftBp ?? V.mourn?.liftBp ?? 5000;
  st.npcs.forEach((n, i) => {
    const k = keepAt(V, n.id);
    if (away(o, n.id)) {
      Object.assign(n, { x: V.away.x, y: V.away.y, moving: false, away: true });
      return;
    }
    if (n.away) delete n.away;
    if (n.hp <= 0) {
      n.moving = false;
      // Down: the hero beside them a moment gets them up (Piet and Ludo are the stretcher's own: battle/litter.ts).
      if (n.id === 'piet' || n.id === 'ludo' || safe(s, i)) return;
      if (h && withinRange(h.x, h.y, n.x, n.y, 1400)) {
        v.lift[i] = (v.lift[i] ?? 0) + 1;
        if ((v.lift[i] as number) >= lift) {
          v.lift[i] = 0;
          n.hp = imax(1, mulBp(n.maxHp, liftBp));
          ev(s, { t: 'senda', kind: 'lifted', npc: n.id, x: n.x, y: n.y });
        }
      } else v.lift[i] = 0;
      return;
    }
    if (!k) return;
    if (G && fights(V, n.id)) {
      v.swing[i] = guardTick(s, n, k, G, v.swing[i] ?? 0, (e) => e.id === o.m.boss || v.tongues.some((t) => t.id === e.id) || v.onPeople.some((x) => x.id === e.id) || o.m.onMirror.some((x) => x.id === e.id) || o.w.limpets.some((l) => l.id === e.id));
      return;
    }
    // The rest keep their place (walking back to it if moved).
    if (withinRange(n.x, n.y, k.x, k.y, 120)) {
      n.moving = false;
      n.x = k.x;
      n.y = k.y;
    } else {
      n.moving = true;
      stepToward(n, k.x, k.y, 70);
    }
  });
  // The blows on the people: each nightmare by a standing person lands one on it, now and then.
  const blow = V.procession?.bite ?? V.mourn?.bite ?? 24;
  if (s.tick % blow !== 0) return;
  for (const e of s.enemies) {
    if (e.hp <= 0 || e.id === o.m.boss || v.tongues.some((t) => t.id === e.id) || v.onLamp.some((x) => x.id === e.id) || v.onPeople.some((x) => x.id === e.id) || o.m.onMirror.some((x) => x.id === e.id) || o.w.limpets.some((l) => l.id === e.id)) continue;
    let best: SendaNpc | null = null;
    let bd = BLOW_R * BLOW_R + 1;
    for (let i = 0; i < st.npcs.length; i++) {
      const n = st.npcs[i] as SendaNpc;
      if (n.hp <= 0 || safe(s, i) || n.away || n.id === 'tessa') continue;
      const d = dist2(e.x, e.y, n.x, n.y);
      if (d < bd) {
        bd = d;
        best = n;
      }
    }
    if (!best) continue;
    hurt(s, best, 1, cue);
  }
};

/** A person takes blows; down, its cue. */
const hurt = (s: BattleState, n: SendaNpc, k: number, cue: (c: string) => void): void => {
  if (n.hp <= 0) return;
  n.hp = imax(0, n.hp - k);
  n.flinch = 6;
  ev(s, { t: 'senda', kind: n.hp > 0 ? 'hit' : 'down', npc: n.id, x: n.x, y: n.y });
  if (n.hp <= 0) cue(`${n.id}Down`);
};

/* ---------- act 1: the lamps ---------- */

const lampsTick = (s: BattleState, o: O, h: Hero | undefined, cue: (c: string) => void): void => {
  const { H, V, v } = o;
  const A = V.lamp;
  if (!A || v.done >= 0) return;
  // The dead at a lamp put it out with their blows.
  if (s.tick % A.blow === 0)
    for (let i = 0; i < V.lamps.length; i++) {
      if (v.lamp[i] !== 1) continue;
      const p = V.lamps[i] as Pt;
      let n = 0;
      for (const e of s.enemies) if (e.hp > 0 && e.kind !== 'dragger' && e.kind !== 'gargoyle' && withinRange(e.x, e.y, p.x, p.y, 750)) n++;
      if (!n) continue;
      v.lampHits[i] = (v.lampHits[i] ?? 0) + n;
      ev(s, { t: 'senda', kind: 'hit', npc: `lamp${i}`, x: p.x, y: p.y });
      if ((v.lampHits[i] as number) >= A.hits) snuff(s, o, i, cue);
    }
  // The limpets on a lamp: held long enough, it goes out (and they crawl on to the next lit one).
  v.onLamp = v.onLamp.filter((x) => s.enemies.some((e) => e.id === x.id && e.hp > 0));
  for (const x of v.onLamp) {
    if (x.at < 0) continue;
    if (v.lamp[x.lamp] !== 1) {
      x.at = -1;
      x.lamp = litNear(V, v, s.enemies.find((e) => e.id === x.id) ?? null);
      continue;
    }
    if (s.tick - x.at >= A.limpet.snuff) snuff(s, o, x.lamp, cue);
  }
  // Out of the ink: a limpet for a lit lamp.
  if (s.waveT >= v.next) {
    v.next = s.waveT + A.limpet.every;
    if (v.onLamp.length < A.limpet.cap && v.lamp.some((x) => x === 1)) {
      const p = H.ink[rngInt(s.rng, H.ink.length)] as Pt;
      const e = spawnEnemy(s, 'dragger', 1, false, 0, p.x, p.y, A.limpet.hp);
      e.speed = A.limpet.speed;
      e.anim = 'idle';
      v.onLamp.push({ id: e.id, lamp: litNear(V, v, e), at: -1 });
      ev(s, { t: 'senda', kind: 'limpetOut', npc: '', x: p.x, y: p.y });
    }
  }
  // The dead in from the cells; gargoyles down by the roof's hole; a bone singer by the atrium.
  if (s.waveT >= v.dead) {
    v.dead = s.waveT + A.dead.every;
    const n = rngRange(s.rng, A.dead.n[0], A.dead.n[1]);
    for (let k = 0; k < n; k++) {
      const c = H.cells[(k + idiv(s.waveT, A.dead.every)) % H.cells.length] as Pt;
      scaled(s, spawnEnemy(s, 'skeleton', 1, false, 0, c.x, c.y + k * 300));
    }
  }
  if (s.waveT >= v.garg) {
    v.garg = s.waveT + A.garg.every;
    for (let k = 0; k < A.garg.n; k++) scaled(s, spawnEnemy(s, 'gargoyle', 1, false, 0, V.roof.x + (k * 2 - 1) * 500, V.roof.y));
    ev(s, { t: 'senda', kind: 'roof', npc: '', x: V.roof.x, y: V.roof.y });
    cue('roof');
  }
  if (!v.singer && s.waveT >= A.singer) {
    const e = scaled(s, spawnEnemy(s, 'bonesinger', 1, false, 0, H.atrium.x, H.atrium.y - 600));
    v.singer = e.id;
  }
  void h;
};

/** The nearest lit lamp to a nightmare (−1: none). */
const litNear = (V: VelaRules, v: VelState, e: Enemy | null): number => {
  let best = -1;
  let bd = NEVER;
  V.lamps.forEach((p, i) => {
    if (v.lamp[i] !== 1) return;
    const d = e ? dist2(e.x, e.y, p.x, p.y) : i;
    if (d < bd) {
      bd = d;
      best = i;
    }
  });
  return best;
};

const snuff = (s: BattleState, o: O, i: number, cue: (c: string) => void): void => {
  const { V, v } = o;
  if (v.lamp[i] !== 1) return;
  v.lamp[i] = 2;
  v.lampHits[i] = 0;
  const p = V.lamps[i] as Pt;
  ev(s, { t: 'senda', kind: 'snuffed', npc: '', x: p.x, y: p.y, n: i });
  cue('snuffed');
};

/* ---------- act 2: the atrium ---------- */

const procTick = (s: BattleState, o: O, cue: (c: string) => void): void => {
  const { V, v } = o;
  const P = V.procession;
  if (!P || v.done >= 0) return;
  // A band in by the cliff's door: the dead, a bone singer leading every few, a mourner among every other.
  if (s.waveT >= v.bandAt && s.waveT <= P.band.until) {
    v.bandAt = s.waveT + P.band.every;
    const b = v.band++;
    const n = rngRange(s.rng, P.band.n[0], P.band.n[1]);
    const kinds: string[] = [];
    if (b % P.singerEvery === P.singerEvery - 1) kinds.push('bonesinger');
    if (b % P.mournerEvery === 1) kinds.push('mourner');
    for (let k = 0; k < n; k++) kinds.push('skeleton');
    kinds.forEach((kind, k) => scaled(s, spawnEnemy(s, kind as 'skeleton', 1, false, 0, P.gate.x + ((k % 3) - 1) * 450, P.gate.y + idiv(k, 3) * 400)));
    ev(s, { t: 'senda', kind: 'band', npc: '', x: P.gate.x, y: P.gate.y, n: b });
    if (kinds[0] === 'bonesinger') cue('singer');
    if (s.waveT + P.band.every > P.band.until) cue('last');
  }
  // At the nave's door: its blows.
  if (s.tick % P.blow === 0) {
    let n = 0;
    for (const e of s.enemies) if (e.hp > 0 && withinRange(e.x, e.y, P.door.x, P.door.y, 950)) n++;
    if (n) {
      const was = v.door;
      v.door = imax(0, v.door - n);
      ev(s, { t: 'senda', kind: 'hit', npc: 'door', x: P.door.x, y: P.door.y, n: v.door });
      if (was * 2 > P.doorHits && v.door * 2 <= P.doorHits) cue('door');
    }
  }
  if (s.waveT > P.band.until && !s.enemies.some((e) => e.hp > 0)) {
    v.done = s.tick;
    ev(s, { t: 'senda', kind: 'end', npc: '', x: P.door.x, y: P.door.y });
  }
};

/** How many bands the procession has. */
export const velBands = (P: NonNullable<VelaRules['procession']>): number => imax(1, idiv(P.band.until - P.band.first, P.band.every) + 1);

/* ---------- act 4: the Doliente ---------- */

/** The beam's pieces bright enough to burn (world points x, y, x, y each). */
const bright = (m: ErmState): number[] => {
  const out: number[] = [];
  for (let i = 0; i + 3 < m.segs.length; i += 4) if ((m.weak[i / 4] ?? 0) < 2) out.push(m.segs[i] as number, m.segs[i + 1] as number, m.segs[i + 2] as number, m.segs[i + 3] as number);
  return out;
};

const segD2 = (px: number, py: number, ax: number, ay: number, bx: number, by: number): number => {
  const dx = bx - ax;
  const dy = by - ay;
  const len2 = dx * dx + dy * dy;
  let t = len2 > 0 ? idiv(((px - ax) * dx + (py - ay) * dy) * 1000, len2) : 0;
  t = t < 0 ? 0 : t > 1000 ? 1000 : t;
  return dist2(px, py, ax + idiv(dx * t, 1000), ay + idiv(dy * t, 1000));
};

/** Whether a point stands in the beam (bright pieces only), within `r`. */
export const velInBeam = (m: ErmState, x: number, y: number, r = 500): boolean => {
  const b = bright(m);
  for (let i = 0; i + 3 < b.length; i += 4) if (segD2(x, y, b[i] as number, b[i + 1] as number, b[i + 2] as number, b[i + 3] as number) <= r * r) return true;
  return false;
};

/** Where she floats to: off the candle toward the door (before the hunt), or between the hero and the altar. */
const dolGoal = (V: VelaRules, h: Hero | undefined, hunting: boolean): Pt => {
  const M = V.mourn as NonNullable<VelaRules['mourn']>;
  const c = V.candle;
  if (hunting && h) {
    const d = imax(1, isqrt(dist2(h.x, h.y, c.x, c.y)));
    const k = imin(d, 1400);
    return { x: h.x + idiv((c.x - h.x) * k, d), y: h.y + idiv((c.y - h.y) * k, d) };
  }
  const door = V.keeps.find((x) => x.id === 'piet') ?? { x: 0, y: 6000 };
  const d = imax(1, isqrt(dist2(door.x, door.y, c.x, c.y)));
  return { x: c.x + idiv((door.x - c.x) * M.keep, d), y: c.y + idiv((door.y - c.y) * M.keep, d) };
};

/** Who her limpets go for at the door: a standing one of Piet and the apprentices (or the hero). */
const doorPerson = (s: BattleState, o: O, k: number): string => {
  const ids = ['piet', 'apprenticeA', 'apprenticeB'].filter((id) => up(person(s, id)));
  return ids.length ? (ids[k % ids.length] as string) : '';
};

const mournTick = (s: BattleState, o: O, h: Hero | undefined, cue: (c: string) => void, rebeam: () => void): void => {
  const { r, H, V, w, m, v } = o;
  const M = V.mourn;
  const N = H.nave;
  if (!M || !N) return;
  if (s.waveT >= M.dawn || v.candle === 0) return;
  // She comes in by the nave's door.
  if (!m.boss) {
    const d = hexToWorld(H.door.q, H.door.r);
    const e = spawnEnemy(s, 'wraith', 3, false, 0, d.x, d.y - 1200, M.hp);
    m.boss = e.id;
    m.wail = M.wail.first;
    ev(s, { t: 'senda', kind: 'bossIn', npc: '', x: e.x, y: e.y });
    cue('in');
  }
  const b = s.enemies.find((e) => e.id === m.boss && e.hp > 0);
  if (!b) return;
  // Control holds her a third as long.
  if (b.stun > 0) b.stun = imax(0, b.stun - 2);
  v.cut = imin(3, idiv((b.maxHp - b.hp) * 4, imax(1, b.maxHp)));
  const st = s.senda;
  if (st && (b.hp * 2 <= b.maxHp || s.waveT >= M.sun)) st.half = true;
  // The split: the veil in two, Ludo and the muleteers in by the atrium.
  if (v.phase === 0 && s.waveT >= M.split) {
    v.phase = 1;
    v.came = 1;
    for (const id of VEL_COMERS) {
      const n = person(s, id);
      if (n) Object.assign(n, { x: V.away.x + (VEL_COMERS.indexOf(id) - 1) * 500, y: V.away.y });
    }
    ev(s, { t: 'senda', kind: 'split', npc: '', x: b.x, y: b.y });
    cue('split');
    cue('come');
  }
  // The first light; then the hunt.
  if (v.sun < 0 && s.waveT >= M.sun) {
    v.sun = m.altar && m.door ? 1 : 0;
    const c = hexToWorld(H.lantern.q, H.lantern.r);
    ev(s, { t: 'senda', kind: 'sun', npc: '', x: c.x, y: c.y, n: v.sun });
    cue(v.sun ? 'sun' : 'sunMiss');
  } else if (v.sun === 0 && m.altar && m.door) {
    v.sun = 1;
    cue('sun');
  }
  if (v.phase < 2 && s.waveT >= M.hunt) {
    v.phase = 2;
    cue('hunt');
  }
  const sunOn = v.sun === 1;
  const inSun = sunOn && velInBeam(m, b.x, b.y, 650);
  // Her wail: warned, then in. Cut by holy light in it, or by the sun. Not cut, the candle shrinks.
  if (v.phase < 2 && m.wailUntil > 0 && s.waveT === m.wailUntil && m.holy < M.wail.hush && !inSun) {
    m.fast++;
    cue('small');
  }
  if (s.waveT === m.wail - M.wail.warn) ev(s, { t: 'senda', kind: 'wailWarn', npc: '', x: b.x, y: b.y, n: M.wail.warn });
  if (s.waveT >= m.wail) {
    m.wailUntil = s.waveT + M.wail.long;
    m.holy = 0;
    m.wail = s.waveT + (v.phase >= 1 ? M.wail.every2 : M.wail.every) + (M.believe ? 0 : M.wail.sing);
    ev(s, { t: 'senda', kind: 'wail', npc: '', x: b.x, y: b.y, n: M.wail.long });
    cue('wail1');
    raise(s, o, b);
  }
  // Hunting: outside the sun she weeps without pause (hushed, she is quiet a while; then again).
  if (v.phase === 2 && !inSun && s.waveT >= m.mute && s.waveT >= m.wailUntil) {
    m.wailUntil = s.waveT + M.wail.long;
    m.holy = 0;
    ev(s, { t: 'senda', kind: 'wail', npc: '', x: b.x, y: b.y, n: M.wail.long });
  }
  if (s.waveT < m.wailUntil && (m.holy >= M.wail.hush || inSun)) {
    m.wailUntil = s.waveT;
    m.holy = 0;
    m.mute = s.waveT + (inSun ? 12 : M.wail.mute);
    ev(s, { t: 'senda', kind: 'hush', npc: '', x: b.x, y: b.y });
    cue('hush');
  }
  const quiet = s.waveT < m.mute;
  // Her veil, on the hero within its reach (it trails metres).
  if (h && !quiet && s.waveT >= m.veil && withinRange(h.x, h.y, b.x, b.y, M.veil.r)) {
    m.veil = s.waveT + M.veil.every;
    b.anim = 'attack';
    b.animT = 0;
    damageHero(s, h, mulBp(h.stats.maxHp, M.veil.bp), b, 'shadow', true);
    ev(s, { t: 'senda', kind: 'veil', npc: '', x: h.x, y: h.y });
  }
  // Her embrace: the veil opens in wings (warned), then whoever is close takes it.
  if (v.hug < 0 && s.waveT >= v.hugAt && !quiet) {
    v.hug = s.waveT;
    v.hugAt = s.waveT + M.hug.every;
    ev(s, { t: 'senda', kind: 'hugWarn', npc: '', x: b.x, y: b.y, n: M.hug.warn });
  }
  if (v.hug >= 0 && s.waveT >= v.hug + M.hug.warn) {
    v.hug = -1;
    ev(s, { t: 'senda', kind: 'hug', npc: '', x: b.x, y: b.y });
    if (h && withinRange(h.x, h.y, b.x, b.y, M.hug.r)) damageHero(s, h, mulBp(h.stats.maxHp, M.hug.bp), b, 'shadow', true);
    (st?.npcs ?? []).forEach((n, i) => {
      if (up(n) && n.id !== 'tessa' && !safe(s, i) && withinRange(n.x, n.y, b.x, b.y, M.hug.r)) hurt(s, n, imax(1, mulBp(n.maxHp, M.hug.bp)), cue);
    });
  }
  // Her tongues down the sides to the candle.
  tongues(s, o, b, quiet, sunOn, cue);
  // The limpets on the door's people: crawling to them, then a blow every so often; their one down, on to another.
  peopleLimpets(s, o, h);
  // The mirrors under a limpet stop the beam (from the split one of each wail's goes for one).
  if (m.onMirror.length) m.onMirror = m.onMirror.filter((x) => s.enemies.some((e) => e.id === x.id && e.hp > 0));
  let cov = 0;
  for (const x of m.onMirror) {
    const e = s.enemies.find((q) => q.id === x.id);
    const c = H.mirrors[x.m] as { q: number; r: number };
    const p = hexToWorld(c.q, c.r);
    if (e && withinRange(e.x, e.y, p.x, p.y, 460)) cov |= 1 << x.m;
  }
  if (cov !== m.covered) {
    if (cov & ~m.covered) cue('covered');
    m.covered = cov;
    rebeam();
  }
  // The muleteers' vinegar: the covered mirror of the way nearest the door, cleaned.
  if (v.came && s.waveT >= v.vinegar) {
    v.vinegar = s.waveT + M.vinegar;
    const dw = hexToWorld(H.door.q, H.door.r);
    let best: { id: number; m: number } | null = null;
    let bd = NEVER;
    for (const x of m.onMirror) {
      if (!((m.covered >> x.m) & 1)) continue;
      const c = H.mirrors[x.m] as { q: number; r: number };
      const p = hexToWorld(c.q, c.r);
      const d = dist2(p.x, p.y, dw.x, dw.y);
      if (d < bd) {
        bd = d;
        best = x;
      }
    }
    if (best && up(person(s, 'arriero'))) {
      const e = s.enemies.find((q) => q.id === best.id);
      if (e) {
        e.hp = 0;
        ev(s, { t: 'senda', kind: 'vinegar', npc: 'arriero', x: e.x, y: e.y, n: best.m });
        cue('vinegar');
      }
    }
  }
  // A mirror of the way frosts over.
  if (v.frost < 0 && s.waveT === M.frost.at) {
    v.frost = M.frost.mirror;
    const c = H.mirrors[v.frost] as { q: number; r: number };
    const p = hexToWorld(c.q, c.r);
    ev(s, { t: 'senda', kind: 'frost', npc: '', x: p.x, y: p.y, n: v.frost });
    cue('frost');
    rebeam();
  }
  // The dead in by the atrium after Ludo's lot, for the door's people.
  if (s.waveT >= v.dead) {
    v.dead = s.waveT + M.dead.every;
    const n = rngRange(s.rng, M.dead.n[0], M.dead.n[1]);
    for (let k = 0; k < n; k++) scaled(s, spawnEnemy(s, 'skeleton', 1, false, 0, H.atrium.x + (k - 1) * 500, H.atrium.y - 300));
  }
  // The beam burns what crosses it once a second (the sun far harder on her tongues; on her, only the sun).
  if (s.tick % 12 === 0) {
    const segs = bright(m);
    if (segs.length >= 4)
      for (const e of s.enemies) {
        if (e.hp <= 0 || m.onMirror.some((x) => x.id === e.id)) continue;
        let on = false;
        for (let i = 0; i + 3 < segs.length && !on; i += 4) on = segD2(e.x, e.y, segs[i] as number, segs[i + 1] as number, segs[i + 2] as number, segs[i + 3] as number) <= 450 * 450;
        if (!on) continue;
        if (e.id === m.boss) {
          if (sunOn) damageEnemy(s, e, M.sunHp, { pid: '', type: 'holy' });
          continue;
        }
        // (A tongue already at the candle is under Tessa's blanket's edge: out of the beam's reach.)
        const tg = v.tongues.find((t) => t.id === e.id);
        if (tg && tg.at >= 0) continue;
        const tongue = !!tg;
        damageEnemy(s, e, tongue ? (sunOn ? M.tongue.sun : M.tongue.lantern) : N.burn, { pid: '', type: 'holy' });
      }
  }
  void r;
  void w;
};

/** The mirrors on the beam's way as set now (bits): Aud's in its place, the frosted one not reflecting. */
const wayNow = (o: O): number => {
  const { H, w, m, v } = o;
  const b = naveTrace({ lantern: H.lantern, altar: H.altar, door: H.door, mirrors: H.mirrors, splits: H.splits }, w.turns, m.splits, v.frost >= 0 ? 1 << v.frost : 0, m.covered);
  return b.touched & 0x3f;
};

/** Each wail's limpets: one for a mirror of the way (from the split), one for Tessa (the truth), the rest for the door's people. */
const raise = (s: BattleState, o: O, b: Enemy): void => {
  const { H, V, w, m, v } = o;
  const M = V.mourn as NonNullable<VelaRules['mourn']>;
  const N = H.nave as NonNullable<HermitRules['nave']>;
  const n = imax(0, M.limpets - v.cut);
  const way = wayNow(o) & ~m.covered;
  for (let k = 0; k < n; k++) {
    const e = spawnEnemy(s, 'dragger', 1, false, 0, b.x + (k - 1) * 450, b.y + 300, N.limpet.hp);
    e.speed = N.limpet.speed;
    e.anim = 'idle';
    ev(s, { t: 'senda', kind: 'limpetOut', npc: '', x: e.x, y: e.y });
    if (k === 0 && v.phase >= 1) {
      // The way's mirrors not already under one: the nearest to her.
      let best = -1;
      let bd = NEVER;
      for (let i = 0; i < H.mirrors.length; i++) {
        if (!((way >> i) & 1) || m.onMirror.some((x) => x.m === i)) continue;
        const c = H.mirrors[i] as { q: number; r: number };
        const p = hexToWorld(c.q, c.r);
        const d = dist2(p.x, p.y, b.x, b.y);
        if (d < bd) {
          bd = d;
          best = i;
        }
      }
      if (best >= 0) {
        m.onMirror.push({ id: e.id, m: best });
        continue;
      }
    }
    if (k === (v.phase >= 1 ? 1 : 0)) {
      // (Let believe, Tessa sleeps: the limpets don't choose her, and the one that would have goes back into the ink.)
      if (!M.believe) w.limpets.push({ id: e.id, on: 0, t: s.tick });
      else e.hp = 0;
      continue;
    }
    const who = doorPerson(s, o, k + v.side);
    if (who) v.onPeople.push({ id: e.id, who, on: 0 });
    else w.limpets.push({ id: e.id, on: 3, t: s.tick });
  }
};

const tongues = (s: BattleState, o: O, b: Enemy, quiet: boolean, sunOn: boolean, cue: (c: string) => void): void => {
  const { V, m, v } = o;
  const M = V.mourn as NonNullable<VelaRules['mourn']>;
  const before = v.tongues.length;
  v.tongues = v.tongues.filter((t) => s.enemies.some((e) => e.id === t.id && e.hp > 0));
  if (v.tongues.length < before) v.tongueAt = imax(v.tongueAt, s.waveT + M.tongue.gap);
  const lanes = v.phase >= 1 ? [0, 1] : [v.side];
  if (!quiet && s.waveT >= v.tongueAt && (v.phase >= 1 ? v.tongues.length < 2 : v.tongues.length === 0))
    for (const lane of lanes) {
      if (v.tongues.some((t) => t.lane === lane)) continue;
      const e = spawnEnemy(s, 'dragger', 1, false, 0, b.x, b.y + 200, M.tongue.hp);
      e.speed = M.tongue.speed;
      v.tongues.push({ id: e.id, lane, at: -1, fast: 0 });
      ev(s, { t: 'senda', kind: 'tongue', npc: '', x: b.x, y: b.y, n: lane });
      if (v.phase === 0) v.side = 1 - v.side;
      v.tongueAt = s.waveT + M.tongue.gap;
    }
  // Held at the candle long enough, it goes out.
  const nervous = (!up(person(s, 'piet')) || (v.came > 0 && !up(person(s, 'ludo')))) ? M.tongue.nervous : 0;
  for (const t of v.tongues) {
    if (t.at < 0 || s.tick < t.at) continue;
    const need = imax(6, (t.fast ? M.tongue.fast : M.tongue.snuff) - nervous);
    if (s.tick - t.at < need) continue;
    v.candle = 0;
    ev(s, { t: 'senda', kind: 'snuffed', npc: '', x: V.candle.x, y: V.candle.y, n: 6 });
    cue('snuffed');
    return;
  }
  // (The renderer draws chapter 4's tongues: from her to each tip.)
  m.tongues = v.tongues.map((t) => ({ id: t.id, lamp: 5, at: t.at }));
  void sunOn;
};

const peopleLimpets = (s: BattleState, o: O, h: Hero | undefined): void => {
  const { V, w, v } = o;
  const M = V.mourn as NonNullable<VelaRules['mourn']>;
  v.onPeople = v.onPeople.filter((x) => s.enemies.some((e) => e.id === x.id && e.hp > 0));
  if (s.tick % M.bite === 0)
    for (const x of v.onPeople) {
      if (x.on !== 1) continue;
      const n = person(s, x.who);
      if (up(n)) hurt(s, n, 1, (c) => sendaCue(s, c));
    }
  for (let i = v.onPeople.length - 1; i >= 0; i--) {
    const x = v.onPeople[i] as VelState['onPeople'][number];
    if (up(person(s, x.who))) continue;
    const who = doorPerson(s, o, i);
    if (who) {
      x.who = who;
      x.on = 0;
    } else {
      v.onPeople.splice(i, 1);
      if (h) w.limpets.push({ id: x.id, on: 3, t: s.tick });
    }
  }
};

/* ---------- the hooks ---------- */

/** A tick of chapter 5's act (from battle/ermita.ts `ermTick`); `pieceWork` turns the nave's mirrors (chapter 4's). */
export const velTick = (s: BattleState, h: Hero | undefined, cue: (c: string) => void, pieceWork: () => void, rebeam: () => void): void => {
  const o = velOf(s);
  if (!o) return;
  if (!work(s, o, h, cue, rebeam)) pieceWork();
  people(s, o, h, cue);
  if (o.V.kind === 'lamps') lampsTick(s, o, h, cue);
  else if (o.V.kind === 'procession') procTick(s, o, cue);
  else mournTick(s, o, h, cue, rebeam);
};

/** Its nightmares (true: handled here). The stretcher's own limpets (Tessa's, the hero's) are left to battle/litter.ts. */
export const velHold = (s: BattleState, e: Enemy, px: (e: Enemy) => number): boolean => {
  const o = velOf(s);
  if (!o) return false;
  const { V, w, m, v } = o;
  if (w.limpets.some((l) => l.id === e.id)) return false;
  // (Their blows' reload already ticks down in battle/litter.ts `litterHold`: once a tick, never twice.)
  const h = s.heroes.find((x) => x.downTicks === 0);
  // The Doliente: off the candle, or between the hero and the altar.
  if (e.id === m.boss && V.mourn) {
    const g = dolGoal(V, h, v.phase === 2);
    if (stepToward(e, g.x, g.y, V.mourn.glide)) e.anim = s.waveT < m.wailUntil ? 'attack' : 'idle';
    else e.anim = 'walk';
    return true;
  }
  // A tongue down its side, then to the candle; the beam halves its crawl.
  const t = v.tongues.find((x) => x.id === e.id);
  if (t && V.mourn) {
    const M = V.mourn;
    const c = V.candle;
    if (t.at >= 0) {
      e.anim = 'attack';
      e.x = c.x + (t.lane ? 200 : -200);
      e.y = c.y + 180;
      return true;
    }
    e.anim = 'walk';
    const lane = M.lanes[t.lane] ?? [];
    let to: Pt = c;
    for (const p of lane)
      if (p.y < e.y - 150) {
        to = p;
        break;
      }
    let pace = mulBp(M.tongue.speed, 10000 - imin(9000, M.slowBp));
    if (velInBeam(m, e.x, e.y, 450)) pace = idiv(pace, 2);
    if (stepToward(e, to.x, to.y, pace) && to === c) stick(s, o, t);
    else if (to === c && withinRange(e.x, e.y, c.x, c.y, 420)) stick(s, o, t);
    return true;
  }
  // A limpet for a lamp or one of the door's people.
  const lx = v.onLamp.find((x) => x.id === e.id);
  if (lx) {
    const p = lx.lamp >= 0 ? V.lamps[lx.lamp] : undefined;
    if (!p) {
      lx.lamp = litNear(V, v, e);
      e.anim = 'idle';
      return true;
    }
    if (lx.at >= 0) {
      const k = (e.id * 167) % 1024;
      e.x = p.x + idiv((k - 512) * 300, 1024);
      e.y = p.y + 160;
      e.anim = 'attack';
      return true;
    }
    e.anim = 'walk';
    if (stepToward(e, p.x, p.y, px(e)) || withinRange(e.x, e.y, p.x, p.y, 420)) {
      lx.at = s.tick;
      ev(s, { t: 'senda', kind: 'stick', npc: '', x: p.x, y: p.y, n: 5 });
      sendaCue(s, 'limpet');
    }
    return true;
  }
  const lp = v.onPeople.find((x) => x.id === e.id);
  if (lp) {
    const n = person(s, lp.who);
    if (!n) return true;
    if (lp.on === 1) {
      const k = (e.id * 211) % 1024;
      e.x = n.x + idiv((k - 512) * 260, 1024);
      e.y = n.y + 120;
      e.anim = 'attack';
      return true;
    }
    e.anim = 'walk';
    if (stepToward(e, n.x, n.y, px(e)) || withinRange(e.x, e.y, n.x, n.y, 420)) {
      lp.on = 1;
      ev(s, { t: 'senda', kind: 'stick', npc: n.id, x: n.x, y: n.y, n: 3 });
      sendaCue(s, 'limpetPeople');
    }
    return true;
  }
  if (e.stun > 0) return false;
  // The rest: the hero when it is by them (gargoyles always), else their work.
  const reach = NIGHTMARES[e.kind].reach;
  if (h && (e.kind === 'gargoyle' || withinRange(h.x, h.y, e.x, e.y, HOLD))) return chase(s, e, h, reach, px);
  let to: Pt | null = null;
  if (V.kind === 'lamps') {
    const i = litNear(V, v, e);
    to = i >= 0 ? (V.lamps[i] as Pt) : null;
  } else {
    // The nearest standing person who fights (or stands at the door); the atrium's door if none.
    let bd = V.kind === 'procession' ? 2600 * 2600 : NEVER;
    const npcs = s.senda?.npcs ?? [];
    for (let i = 0; i < npcs.length; i++) {
      const n = npcs[i] as SendaNpc;
      if (!up(n) || safe(s, i) || n.id === 'tessa') continue;
      const d = dist2(e.x, e.y, n.x, n.y);
      if (d < bd) {
        bd = d;
        to = n;
      }
    }
    if (!to && V.procession) to = V.procession.door;
  }
  if (!to) return h ? chase(s, e, h, reach, px) : true;
  if (withinRange(e.x, e.y, to.x, to.y, imax(reach + 150, 650))) {
    if (e.cd === 0) {
      e.cd = e.reload;
      e.anim = 'attack';
      e.animT = 0;
    }
    return true;
  }
  e.anim = 'walk';
  stepToward(e, to.x, to.y, px(e));
  return true;
};

const chase = (s: BattleState, e: Enemy, h: Hero, reach: number, px: (e: Enemy) => number): boolean => {
  if (withinRange(e.x, e.y, h.x, h.y, reach + 150)) attackHero(s, e, h);
  else {
    e.anim = 'walk';
    stepToward(e, h.x, h.y, px(e));
  }
  return true;
};

/** A tongue at the candle: Joran holds one now and then (it sticks later). */
const stick = (s: BattleState, o: O, t: VelState['tongues'][number]): void => {
  const { V, m, v } = o;
  const M = V.mourn as NonNullable<VelaRules['mourn']>;
  t.at = s.tick;
  if (m.fast > 0) {
    t.fast = 1;
    m.fast--;
  }
  const j = person(s, 'joran');
  if (up(j) && s.waveT >= v.joran && withinRange(j.x, j.y, V.candle.x, V.candle.y, M.tongue.hold.r + 800)) {
    v.joran = s.waveT + M.tongue.hold.every;
    t.at = s.tick + M.tongue.hold.ticks;
    ev(s, { t: 'senda', kind: 'held', npc: 'joran', x: V.candle.x, y: V.candle.y });
    sendaCue(s, 'hold');
  }
  ev(s, { t: 'senda', kind: 'stick', npc: '', x: V.candle.x, y: V.candle.y, n: 6 });
  sendaCue(s, 'tongue1');
};

/** How far along: the lamps lit; the procession's bands down; the night gone by. */
export const velGoal = (s: BattleState): { done: number; need: number } | null => {
  const o = velOf(s);
  const V = s.rules?.senda?.litter?.hermit?.vela;
  if (!V) return null;
  if (V.kind === 'lamps') return { done: o ? o.v.lamp.filter((x) => x === 1).length : 0, need: V.lamps.length };
  if (V.kind === 'procession' && V.procession) {
    const need = velBands(V.procession);
    if (!o) return { done: 0, need };
    const alive = s.enemies.some((e) => e.hp > 0);
    return { done: o.v.done >= 0 ? need : imax(0, o.v.band - (alive ? 1 : 0)), need };
  }
  const M = V.mourn;
  return M ? { done: imin(M.dawn, s.waveT), need: M.dawn } : null;
};

/** Won or lost by its own rule. */
export const velResult = (s: BattleState): 'won' | 'lost' | null => {
  const o = velOf(s);
  if (!o) return null;
  const { V, m, v } = o;
  if (V.kind === 'lamps') {
    if (v.lamp.filter((x) => x === 2).length >= 2) return 'lost';
    return v.done >= 0 && s.tick - v.done >= (V.lamp?.settle ?? 0) ? 'won' : null;
  }
  if (V.kind === 'procession') {
    if (v.door <= 0) return 'lost';
    return v.done >= 0 && s.tick - v.done >= (V.procession?.settle ?? 0) ? 'won' : null;
  }
  const M = V.mourn;
  if (!M) return null;
  if (v.candle === 0) return 'lost';
  // Down (her body is gone from the field once she falls): won.
  if (m.boss && !s.enemies.some((e) => e.id === m.boss && e.hp > 0)) {
    if (!s.senda?.cues?.includes('fell')) {
      ev(s, { t: 'senda', kind: 'dawn', npc: '', x: V.candle.x, y: V.candle.y });
      sendaCue(s, 'fell');
    }
    return 'won';
  }
  if (s.waveT >= M.dawn) {
    if (s.waveT === M.dawn) {
      ev(s, { t: 'senda', kind: 'dawn', npc: '', x: V.candle.x, y: V.candle.y });
      sendaCue(s, 'dawn');
    }
    return 'won';
  }
  return null;
};
